
import { IDriver } from "@interfaces/IDriver";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleDriverMapping } from "@interfaces/IVehicleDriverMapping";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import "angular-material";
import * as moment from "moment";
import { DriverAssignmentSource } from "../enum/DriverAssignmentSource.enum";
import { CacheService } from "./cache.Service";
import { DriverService } from "./driver.service";
import { TagsService } from "./tags.service";
import { IGetVehicleOptions, VehicleService } from "./vehicle.service";
import { VeriDate } from "./veriDate.service";

export class DriverAssignmentService {

    private driverAssignmentCacheName = "activeDriverAssignmentCacheName"

    private permanentEndDate = moment(new Date(2200, 0, 1));

    static $inject = ["$q", "$http", "driverService", "vehicleService", "tagsService", "veriDate", "translationService", "cacheService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly driverService: DriverService,
        private readonly vehicleService: VehicleService,
        private readonly tagsService: TagsService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
        private readonly cacheService: CacheService
    ) {
        this.cacheService.createCache(this.driverAssignmentCacheName, { maxAge: 30 * 60 * 1000 });
    }

    getSourceString(assignmentSource: DriverAssignmentSource) {
        switch (assignmentSource) {
            case DriverAssignmentSource.DigiDl:
                return this.translationService.t("driverAssignmentService.tachoDigiDl");

            case DriverAssignmentSource.Unit:
                return this.translationService.t("driverAssignmentService.tachoKline");

            case DriverAssignmentSource.ButtonPress:
                return this.translationService.t("driverAssignmentService.buttonPress");

            case DriverAssignmentSource.ManualAssignment:
                return this.translationService.t("driverAssignmentService.manual");
        }

        return this.translationService.t("driverAssignmentService.unknown");
    }

    getVehiclesWithDrivers(options?: IGetVehicleOptions): angular.IPromise<IVehicle[]> {

        const vehiclePromise = this.vehicleService.getVehicles(options);
        const driversPromise = this.driverService.getDrivers({ invalidateCache: options?.invalidateCache });
        const assignmentPromise = this.getDriverAssignments(true, options?.invalidateCache);

        return this.$q
            .all([vehiclePromise, driversPromise, assignmentPromise])
            .then(([vehicles, drivers, assignments]) => {
                this.mapDriversToVehicles(drivers, assignments, vehicles);
                return vehicles;
            });
    }

    getDriversWithVehicles(): angular.IPromise<IDriver[]> {

        const vehiclePromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const driversPromise = this.driverService.getDrivers();
        const assignmentPromise = this.getDriverAssignments(true);

        return this.$q
            .all([vehiclePromise, driversPromise, assignmentPromise])
            .then(([vehicles, drivers, assignments]) => {
                this.mapDriversToVehicles(drivers, assignments, vehicles);

                return drivers;
            });
    }


    getDriverAssignments(activeOnly: boolean, invalidateCache = false): angular.IPromise<IVehicleDriverMapping[]> {
        return this.cacheService.getOrRetrieveData<IVehicleDriverMapping[]>(this.driverAssignmentCacheName, () => this.getDriverAssignmentsFromDB(), invalidateCache).then(assignments => {
            if (activeOnly) {
                assignments = assignments.filter(mapping => this.veriDate.createMoment(mapping.EndUtc).isAfter(this.veriDate.createMoment(new Date())));
            }

            return assignments;
        });
    }

    getDriverAssignmentsFromDB(): angular.IPromise<IVehicleDriverMapping[]> {
        return this
            .$http({
                url: "/api/driverassignment/latest",
                method: "GET",
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => response.data);
    }

    getActiveAssignmentsForVehicleFromDB(vehicleId: number): angular.IPromise<IVehicleDriverMapping[]> {
        return this
            .$http({
                url: `/api/driverassignment/vehicles/${vehicleId}/active`,
                method: "GET",
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => response.data);
    }

    getDriverAssignmentsByDriverForTimeRange(driverId: number, from: moment.Moment, to: moment.Moment): angular.IPromise<IVehicleDriverMapping[]> {
        let url = `/api/driverassignment/driver/${driverId}?from=${from.toISOString()}&to=${to.toISOString()}`;

        return this
            .$http({
                url: url,
                method: "GET",
                cache: true,
                params: { minsToLive: 10 }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => {
                return response.data;
            });
    }

    getAssignmentByDriverForTime(driverId: number, time: moment.Moment, logging = false): angular.IPromise<IVehicle> {

        let url = `/api/driverassignment/driver/${driverId}?from=${time.toISOString()}`;

        var driverSpecificAssignmentsPromise = this
            .$http({
                url: url,
                method: "GET",
                cache: true,
                params: { minsToLive: 60 }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => {
                return response.data;
            });

        var vehiclePromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });

        return this.$q
            .all([vehiclePromise, driverSpecificAssignmentsPromise])
            .then(([vehicles, driverAssignments]) => {

                if (logging) {
                    console.log(driverAssignments);
                }

                const mapping = this.findAssignmentByDriverForTime(driverId, time, driverAssignments);

                if (!mapping) {
                    return null;
                }

                return this.vehicleService.findVehicle(vehicles, mapping.VehicleId);
            });
    }

    getIntersectingAssignmentsByVehicle(vehicleId: number, start: moment.Moment, end: moment.Moment) {
        const url = `/api/driverassignment/vehicle/${vehicleId}/intersecting`;

        return this
            .$http({
                url: url,
                method: "GET",
                cache: true,
                params: {
                    minsToLive: 60,
                    start: start.toISOString(),
                    end: end.toISOString()
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => {
                this.formatAssignments(response.data);

                return response.data;
            });

    }

    getAssignmentsByVehicleForTime(vehicleId: number, time?: moment.Moment): angular.IPromise<IVehicleDriverMapping[]> {

        let url = `/api/driverassignment/vehicle/${vehicleId}`;
        if (time && !moment.isMoment(time)) {
            time = moment(time);
        }

        if (time) {
            url += `?time=${time.toISOString()}`;
        }

        return this
            .$http({
                url: url,
                method: "GET",
                cache: true,
                params: { minsToLive: 60 }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleDriverMapping[]>) => {

                this.formatAssignments(response.data);

                return response.data;
            });
    }

    getVehicleDriverAssignmentsForDateRange(vehicleId: number, start: moment.Moment, end: moment.Moment): angular.IPromise<IVehicleDriverMapping[]> {

        return this
            .getAssignmentsByVehicleForTime(vehicleId, null)
            .then((assignments) => {
                const filteredAssignments: IVehicleDriverMapping[] = [];

                if (assignments) {
                    for (let i = 0; i < assignments.length; i++) {
                        const assignment = assignments[i];

                        // Check for overlap
                        var overlapped = assignment.StartUtc.isSameOrBefore(end) && assignment.EndUtc.isSameOrAfter(start);

                        if (overlapped) {
                            filteredAssignments.push(assignment);
                        }
                    }
                }

                return filteredAssignments;
            });
    }

    getIntersectingDriverAssignmentsForVehicle(vehicleId: number, start: moment.Moment, end: moment.Moment): angular.IPromise<[IDriver[], IVehicleDriverMapping[]]> {

        const driversPromise = this.driverService.getDrivers();
        const vehicleSpecificAssignmentsPromise = this.getIntersectingAssignmentsByVehicle(vehicleId, start, end);

        return this.$q.all([driversPromise, vehicleSpecificAssignmentsPromise]);
    }

    getDriverAssignmentsForVehicle(vehicleId: number, time?: moment.Moment): angular.IPromise<[IDriver[], IVehicleDriverMapping[]]> {

        const driversPromise = this.driverService.getDrivers();
        const vehicleSpecificAssignmentsPromise = this.getAssignmentsByVehicleForTime(vehicleId, time);

        return this.$q.all([driversPromise, vehicleSpecificAssignmentsPromise]);
    }

    getDriverAssignmentForTime(vehicleId: number, time: moment.Moment | string | Date): angular.IPromise<IDriver> {
        let timeMoment = this.veriDate.createMoment(time);

        return this.getDriverAssignmentsForVehicle(vehicleId, timeMoment)
            .then(([drivers, vehicleAssignments/*, allAssignments*/]) => {
                const mapping = this.findAssignmentByVehicleForTime(vehicleId, timeMoment, vehicleAssignments);

                if (!mapping) {
                    return null;
                }

                return this.driverService.findDriver(drivers, mapping.DriverId);
            });
    }

    saveDriverAssignment(driverAssignment: IVehicleDriverMapping): angular.IPromise<any> {

        const promise = this.$http(
            {
                method: "POST",
                url: "/api/driverassignment",
                data: driverAssignment
            })
            .then((result: angular.IHttpPromiseCallbackArg<any>) => {

                this.cacheService.addOrUpdateItem<IVehicleDriverMapping>(this.driverAssignmentCacheName, driverAssignment, (i1, i2) => i1.Id == i2.Id);

                return result;
            });

        return promise;
    }

    endDriverAssignment(driverAssignment: IVehicleDriverMapping): angular.IPromise<any> {

        var promise = this.$http(
            {
                method: "POST",
                url: "/api/driverassignment/endassignment",
                data: driverAssignment
            })
            .then((result: angular.IHttpPromiseCallbackArg<any>) => {
                // since it's an active assignments cache - ended assignment must be deleted
                this.cacheService.deleteItem<IVehicleDriverMapping>(this.driverAssignmentCacheName, driverAssignment, (i1, i2) => i1.Id == i2.Id);

                return result;
            });

        return promise;
    }

    updateDriver(driver: IDriver) {

        const promise = this.$http(
            {
                method: "POST",
                url: "/api/drivers",
                data: driver
            })
            .then((result: angular.IHttpPromiseCallbackArg<number>) => {
                return result.data;
            });

        return promise;
    }

    mapDriversToVehicles(drivers: IDriver[], mappings: IVehicleDriverMapping[], vehicles: IVehicle[]) {
        for (let mappingIndex = 0; mappingIndex < mappings.length; mappingIndex++) {

            const mapping = mappings[mappingIndex];

            const driver = drivers.find(driver => driver.Id == mapping.DriverId);
            const vehicle = vehicles.find(vehicle => vehicle.Id == mapping.VehicleId);

            if (driver) {
                if (!driver.VehicleDriverMapping) {
                    driver.VehicleDriverMapping = [];
                }

                driver.VehicleDriverMapping.push(mapping);

                if (vehicle) {
                    vehicle.Driver = driver;
                    driver.vehicle = vehicle;
                }
            }

            if (vehicle && !driver) {
                vehicle.unkownDriver = true;
            }
        }
    }

    findAssignmentByDriverId(mappings: IVehicleDriverMapping[], driverId: number): IVehicleDriverMapping {
        for (let i = 0; i < mappings.length; i++) {
            const mapping = mappings[i];

            if (mapping.DriverId === driverId) {
                return mapping;
            }
        }

        return null;
    }

    findAssignmentByVehicleId(mappings: IVehicleDriverMapping[], vehicleId: number): IVehicleDriverMapping {
        for (let i = 0; i < mappings.length; i++) {
            const mapping = mappings[i];

            if (mapping.VehicleId === vehicleId) {
                return mapping;
            }
        }

        return null;
    }

    findAssignmentByVehicleForTime(vehicleId: number, time: moment.Moment, assignments: IVehicleDriverMapping[]): IVehicleDriverMapping {
        if (assignments) {
            for (let i = 0; i < assignments.length; i++) {
                const assignment = assignments[i];

                if (assignment.VehicleId == vehicleId && time && time.isSameOrAfter(assignment.StartUtc) && time.isSameOrBefore(assignment.EndUtc)) {
                    return assignment;
                }
            }
        }

        return null;
    }

    findAssignmentByDriverForTime(driverId: number, time: moment.Moment, assignments: IVehicleDriverMapping[]): IVehicleDriverMapping {
        if (assignments) {
            for (let i = 0; i < assignments.length; i++) {
                const assignment = assignments[i];

                if (assignment.DriverId == driverId && time && time.isSameOrAfter(assignment.StartUtc) && time.isSameOrBefore(assignment.EndUtc)) {
                    return assignment;
                }
            }
        }

        return null;
    }

    getAssignmentPermanentDate(safe = true): moment.Moment {

        // Little performance improvement if we dont need to be safe
        if (!safe) {
            return this.permanentEndDate;
        }

        return this.permanentEndDate.clone();
    }

    assignmentsAreSame(currentAssignment: IVehicleDriverMapping, currentAssignmentCopy: IVehicleDriverMapping): boolean {

        if (currentAssignment == currentAssignmentCopy) {
            return true;
        }

        if (currentAssignment && !currentAssignmentCopy || !currentAssignment && currentAssignmentCopy) {
            return false;
        }

        if (currentAssignment.DriverId != currentAssignmentCopy.DriverId) {
            return false;
        }

        if (currentAssignment.VehicleId != currentAssignmentCopy.VehicleId) {
            return false;
        }

        if (currentAssignment.AssignmentSource != currentAssignmentCopy.AssignmentSource) {
            return false;
        }

        if (!moment(currentAssignment.StartUtc).isSame(currentAssignmentCopy.StartUtc)) {
            return false;
        }

        if (!moment(currentAssignment.EndUtc).isSame(currentAssignmentCopy.EndUtc)) {
            return false;
        }

        return true;
    }

    assignmentIsPermanent(mapping: IVehicleDriverMapping): boolean {
        if (!mapping) {
            return false;
        }

        return moment(mapping.EndUtc).isSame(this.getAssignmentPermanentDate(false), "d");
    }

    getAssignmentEndLabel(mapping: IVehicleDriverMapping): string {
        if (!mapping) {
            return "";
        }

        if (this.assignmentIsPermanent(mapping)) {
            return this.translationService.t("driverAssignmentService.noEndDate");
        }

        return this.veriDate.formatDate(mapping.EndUtc, "date");
    }

    // ---

    private formatAssignments(assignments: IVehicleDriverMapping[]) {
        assignments.forEach(assignment => {
            assignment.StartUtc = moment(assignment.StartUtc);
            assignment.EndUtc = moment(assignment.EndUtc);
        });
    }
}
